import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Header from '../../components/Header';
import SearchableSelect from '../../components/SearchableSelect';
import { styles, colors } from '../../styles/theme';
import { invalidateQueries } from '../../hooks/useCachedQuery';
import { buildZayiOptions, parseZayiValue } from '../../utils/zayiOptions';
import { filtreleZayiKayitlar, siralaZayiKayitlar, zayiKayitlarToCSV } from '../../utils/zayiKayitlar';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const getAuthHeaders = (extra = {}) => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}`, ...extra } : extra;
};

// Ağırlık birimleri — adet↔kg karışması yalnız burada anlamlı (AD/PORSIYON zaten net)
const WEIGHT_UNITS = ['KG', 'GR', 'GRAM', 'KILOGRAM'];
const isWeightUnit = (b) => WEIGHT_UNITS.includes(String(b || '').toUpperCase());
const isKgUnit = (b) => String(b || '').toUpperCase().startsWith('K');

// "K12" / "H34" → 12 / 34 ; düzenlenebilirlik: yalnız ZayiKaydi (K) — legacy (H) endpoint yok
const numericRowId = (rowId) => parseInt(String(rowId).replace(/^[KH]/, ''), 10);
const isEditableRow = (row) => String(row?.id || '').startsWith('K');

const miniInput = {
  width: 64, padding: '4px 8px', border: '1px solid #d9d9d9', borderRadius: 5,
  fontSize: 12, boxSizing: 'border-box',
};
const miniBtn = {
  padding: '4px 10px', border: 'none', borderRadius: 5, background: '#ad6800',
  color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer',
};
const rowBtnStyle = (bg) => ({
  padding: '4px 10px', border: 'none', borderRadius: 5, background: bg,
  color: '#fff', fontSize: 11, fontWeight: 600, cursor: 'pointer',
});

// Adet → kg/gr çevirici (kök-neden önleme): adet × gram/adet → kalemin birimi.
// Ağırlık-dışı birimde gizli. onApply hesaplanan değeri (kalemin biriminde) döndürür.
const AdetKgHelper = ({ birim, onApply }) => {
  const [adet, setAdet] = useState('');
  const [gram, setGram] = useState('');
  if (!isWeightUnit(birim)) return null;
  const a = parseFloat(adet);
  const g = parseFloat(gram);
  const kg = isKgUnit(birim);
  const sonuc = a > 0 && g > 0 ? (kg ? (a * g) / 1000 : a * g) : null;
  return (
    <div style={{ marginTop: 8, padding: '8px 12px', background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 6 }}>
      <span style={{ fontWeight: 600, color: '#ad6800', fontSize: 12 }}>📐 Adet → {kg ? 'kg' : 'gr'} çevir</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, flexWrap: 'wrap', fontSize: 12 }}>
        <input type="number" min="0" step="1" value={adet} onChange={e => setAdet(e.target.value)} placeholder="adet" style={miniInput} />
        <span>×</span>
        <input type="number" min="0" step="1" value={gram} onChange={e => setGram(e.target.value)} placeholder="g/adet" style={miniInput} />
        <span style={{ color: '#888' }}>g/adet</span>
        {sonuc != null && (
          <>
            <span style={{ fontWeight: 700, color: '#ad6800' }}>= {kg ? sonuc.toFixed(3) : sonuc.toFixed(0)} {kg ? 'kg' : 'gr'}</span>
            <button type="button" onClick={() => onApply(kg ? Number(sonuc.toFixed(3)) : Number(sonuc.toFixed(0)))} style={miniBtn}>Uygula</button>
          </>
        )}
      </div>
    </div>
  );
};

export default function ZayiGirisiPage() {
  const [view, setView] = useState('form'); // 'form' | 'list'
  const [malzemeler, setMalzemeler] = useState([]);
  const [lokasyonlar, setLokasyonlar] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [nedenler, setNedenler] = useState([]);
  const [kayıtlar, setKayıtlar] = useState([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);

  // Form state
  const [lokasyonId, setLokasyonId] = useState('');
  const [secilen, setSecilen] = useState(''); // "m:{id}" (malzeme) veya "r:{id}" (reçete)
  const [miktar, setMiktar] = useState('');
  const [nedenKategoriId, setNedenKategoriId] = useState(''); // zorunlu kategori
  const [notText, setNotText] = useState(''); // opsiyonel serbest metin not
  const [tarih, setTarih] = useState(new Date().toISOString().split('T')[0]);

  // Kayıtlar — filtre/sıralama state
  const [fTarihBas, setFTarihBas] = useState(''); // server-side
  const [fTarihBit, setFTarihBit] = useState(''); // server-side
  const [fLokasyon, setFLokasyon] = useState(''); // server-side
  const [fNeden, setFNeden] = useState('');       // server-side (neden_kategori_id)
  const [fQ, setFQ] = useState('');               // client-side
  const [fTip, setFTip] = useState('');           // client-side ('' | 'malzeme' | 'recete')
  const [sortKey, setSortKey] = useState('tarih');
  const [sortDir, setSortDir] = useState('desc');

  // Sunucu-taraflı filtrelerle kayıtları yükle (useCallback → exhaustive-deps temiz)
  const loadKayıtlar = useCallback(() => {
    const params = new URLSearchParams({ limit: '500' });
    if (fTarihBas) params.set('tarih_baslangic', fTarihBas);
    if (fTarihBit) params.set('tarih_bitis', fTarihBit);
    if (fLokasyon) params.set('lokasyon_id', fLokasyon);
    if (fNeden) params.set('neden_kategori_id', fNeden);
    fetch(`${API_URL}/api/zayi?${params.toString()}`, { headers: getAuthHeaders() })
      .then(r => r.json()).then(data => {
        if (Array.isArray(data)) setKayıtlar(data);
      }).catch(() => {});
  }, [fTarihBas, fTarihBit, fLokasyon, fNeden]);

  // İlk yükleme: master data + neden kategorileri
  useEffect(() => {
    fetch(`${API_URL}/api/malzemeler`, { headers: getAuthHeaders() })
      .then(r => r.json()).then(setMalzemeler).catch(() => {});
    fetch(`${API_URL}/api/lokasyonlar`, { headers: getAuthHeaders() })
      .then(r => r.json()).then(setLokasyonlar).catch(() => {});
    fetch(`${API_URL}/api/recipes`, { headers: getAuthHeaders() })
      .then(r => r.json()).then(data => {
        setRecipes(Array.isArray(data) ? data : (data?.items || []));
      }).catch(() => {});
    fetch(`${API_URL}/api/zayi-nedenleri`, { headers: getAuthHeaders() })
      .then(r => r.json()).then(d => setNedenler(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);

  // Sunucu-taraflı filtreler değişince (ve mount'ta) kayıtları yeniden yükle
  useEffect(() => { loadKayıtlar(); }, [loadKayıtlar]);

  // Malzeme id → reçete marka map'i (tabak/mamül ürünleri için marka göstermek için)
  const markaByMalzemeId = useMemo(() => {
    const map = {};
    (recipes || []).forEach(r => {
      if (r.urun_malzeme_id && r.marka) {
        map[r.urun_malzeme_id] = r.marka;
      }
    });
    return map;
  }, [recipes]);

  // Malzemeler + urun_malzeme_id'si olmayan reçeteler (TABAK) — reçete seçilince
  // zayi edilince hammaddeleri stoktan düşülür.
  const malzemeOptions = useMemo(
    () => buildZayiOptions(malzemeler, recipes, markaByMalzemeId),
    [malzemeler, recipes, markaByMalzemeId]
  );

  const lokasyonOptions = useMemo(() =>
    (lokasyonlar || []).map(l => ({ value: l.id, label: l.ad })),
    [lokasyonlar]
  );

  const selectedOption = malzemeOptions.find(o => o.value === secilen);

  const handleSubmit = async () => {
    const parsed = parseZayiValue(secilen);
    if (!lokasyonId || !parsed || !miktar || !nedenKategoriId) {
      setMsg({ type: 'error', text: 'Tüm zorunlu alanları doldurun (neden dahil)' });
      return;
    }
    setLoading(true);
    setMsg(null);
    try {
      const body = {
        lokasyon_id: lokasyonId,
        miktar: parseFloat(miktar),
        birim: selectedOption?.birim || 'KG',
        neden_kategori_id: nedenKategoriId,
        neden: notText || null,
        tarih: tarih || null,
      };
      if (parsed.kind === 'recipe') body.recipe_id = parsed.id;
      else body.malzeme_id = parsed.id;

      const res = await fetch(`${API_URL}/api/zayi`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Hata');

      invalidateQueries(['stok', 'dashboard']);
      setMsg({
        type: 'success',
        text: `${data.malzeme_ad}: -${data.miktar} ${selectedOption?.birim || 'KG'} zayi kaydedildi${data.uyari ? ' ⚠️' : ''}`,
        uyari: data.uyari
      });
      // Reset form
      setSecilen('');
      setMiktar('');
      setNedenKategoriId('');
      setNotText('');
      loadKayıtlar();
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  };

  // Neden kategorisi seçenekleri (aktif olanlar) — SearchableSelect için
  const nedenOptions = useMemo(
    () => (nedenler || [])
      .filter(n => n.aktif !== false)
      .map(n => ({ value: n.id, label: n.ad, searchText: `${n.ad} ${n.aciklama || ''}` })),
    [nedenler]
  );
  const selectedNeden = useMemo(
    () => (nedenler || []).find(n => n.id === nedenKategoriId),
    [nedenler, nedenKategoriId]
  );

  // Filtrelenmiş + sıralanmış görünüm (Kayıtlar tab)
  const gosterilen = useMemo(
    () => siralaZayiKayitlar(filtreleZayiKayitlar(kayıtlar, { q: fQ, tip: fTip }), sortKey, sortDir),
    [kayıtlar, fQ, fTip, sortKey, sortDir]
  );

  // KPI — filtrelenmiş görünümü yansıtır
  const bugun = new Date().toISOString().split('T')[0];
  const bugunSayisi = useMemo(
    () => gosterilen.filter(r => r.tarih === bugun).length,
    [gosterilen, bugun]
  );

  // Sıralama başlığı tıklama: aynı kolon → yön değiştir, farklı → o kolona geç (desc)
  const toggleSort = (key) => {
    if (key === sortKey) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };
  const sortIndicator = (key) => (key === sortKey ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '');

  // Tüm filtreleri temizle
  const temizleFiltreler = () => {
    setFTarihBas(''); setFTarihBit(''); setFLokasyon(''); setFNeden('');
    setFQ(''); setFTip('');
  };

  // CSV indir (filtrelenmiş görünüm)
  const indirCSV = () => {
    const blob = new Blob([zayiKayitlarToCSV(gosterilen)], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `zayi-kayitlari-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  // --- Düzenle / Sil (stok-güvenli PATCH/DELETE; storno cascade backend'de) ---
  const [editRow, setEditRow] = useState(null);   // düzenlenen kayıt (K-önekli) veya null
  const [eMiktar, setEMiktar] = useState('');
  const [eNedenKat, setENedenKat] = useState('');
  const [eNot, setENot] = useState('');
  const [eTarih, setETarih] = useState('');
  const [deleteRow, setDeleteRow] = useState(null); // silme onayı bekleyen kayıt
  const [actionLoading, setActionLoading] = useState(false);

  const editIsRecipe = !!editRow?.recipe_id; // reçete (TABAK) → miktar düzenlenemez (backend 400)

  const openEdit = (row) => {
    setEditRow(row);
    setEMiktar(row.miktar != null ? String(row.miktar) : '');
    setENedenKat(row.neden_kategori_id || '');
    setENot(row.neden || '');
    setETarih(row.tarih || '');
    setMsg(null);
  };

  const handleEditSave = async () => {
    if (!editRow) return;
    const body = {};
    if (!editIsRecipe && eMiktar !== '') {
      const yeni = parseFloat(eMiktar);
      if (!(yeni > 0)) { setMsg({ type: 'error', text: 'Miktar 0\'dan büyük olmalı' }); return; }
      if (yeni !== editRow.miktar) body.miktar = yeni;
    }
    if ((eNedenKat || null) !== (editRow.neden_kategori_id || null)) body.neden_kategori_id = eNedenKat || null;
    // Not: backend `neden is not None` ile uygular → temizlemek için boş string gönder ('' ≠ null)
    if ((eNot || '') !== (editRow.neden || '')) body.neden = eNot;
    if (eTarih && eTarih !== editRow.tarih) body.tarih = eTarih;
    if (Object.keys(body).length === 0) { setEditRow(null); return; }
    setActionLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/zayi/${numericRowId(editRow.id)}`, {
        method: 'PATCH',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Düzenleme hatası');
      invalidateQueries(['stok', 'dashboard']);
      setMsg({ type: 'success', text: `Zayi kaydı güncellendi${body.miktar != null ? ' (stok yeniden hesaplandı)' : ''}` });
      setEditRow(null);
      loadKayıtlar();
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteRow) return;
    setActionLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/zayi/${numericRowId(deleteRow.id)}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Silme hatası');
      invalidateQueries(['stok', 'dashboard']);
      setMsg({ type: 'success', text: 'Zayi kaydı silindi — stok iade edildi' });
      setDeleteRow(null);
      loadKayıtlar();
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
      setDeleteRow(null);
    } finally {
      setActionLoading(false);
    }
  };

  // Giriş guard'ı: KG birimli kalemde küçük tam-sayı → "kg mı adet mi?" yumuşak uyarı (bloklamaz)
  const girisAdetUyarisi = useMemo(() => {
    if (!selectedOption || !isKgUnit(selectedOption.birim)) return null;
    const n = parseFloat(miktar);
    return Number.isInteger(n) && n >= 1 && n <= 20 ? n : null;
  }, [selectedOption, miktar]);

  return (
    <div style={{ minHeight: '100vh', background: '#f5f7fa' }}>
      <Header />
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px 16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>🗑️ Zayi / Fire Girişi</h1>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => setView('form')}
              style={{
                padding: '8px 16px', borderRadius: 6, border: 'none', cursor: 'pointer',
                background: view === 'form' ? '#1a73e8' : '#e0e0e0',
                color: view === 'form' ? '#fff' : '#333', fontWeight: 600, fontSize: 13
              }}
            >Yeni Kayıt</button>
            <button
              onClick={() => setView('list')}
              style={{
                padding: '8px 16px', borderRadius: 6, border: 'none', cursor: 'pointer',
                background: view === 'list' ? '#1a73e8' : '#e0e0e0',
                color: view === 'list' ? '#fff' : '#333', fontWeight: 600, fontSize: 13
              }}
            >Kayıtlar ({gosterilen.length})</button>
          </div>
        </div>

        {/* KPI — filtrelenmiş görünümü yansıtır */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 20 }}>
          <div style={{ ...styles.card, padding: 16, textAlign: 'center' }}>
            <div style={{ fontSize: 24, fontWeight: 700, color: colors.danger }}>{gosterilen.length}</div>
            <div style={{ fontSize: 12, color: colors.textSecondary }}>Kayıt (filtreli)</div>
          </div>
          <div style={{ ...styles.card, padding: 16, textAlign: 'center' }}>
            <div style={{ fontSize: 24, fontWeight: 700, color: colors.danger }}>{bugunSayisi}</div>
            <div style={{ fontSize: 12, color: colors.textSecondary }}>Bugün Zayi</div>
          </div>
        </div>

        {/* Mesaj */}
        {msg && (
          <div style={{
            padding: '12px 16px', borderRadius: 8, marginBottom: 16,
            background: msg.type === 'success' ? '#e8f5e9' : '#ffebee',
            color: msg.type === 'success' ? '#2e7d32' : '#c62828',
            border: `1px solid ${msg.type === 'success' ? '#a5d6a7' : '#ef9a9a'}`
          }}>
            {msg.text}
            {msg.uyari && <div style={{ marginTop: 4, fontSize: 13, color: '#e65100' }}>⚠️ {msg.uyari}</div>}
          </div>
        )}

        {view === 'form' && (
          <div style={{ ...styles.card, padding: 24 }}>
            <h3 style={{ marginTop: 0, marginBottom: 20, fontSize: 16, fontWeight: 600 }}>Yeni Zayi Kaydı</h3>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#333' }}>Lokasyon *</label>
                <SearchableSelect
                  options={lokasyonOptions}
                  value={lokasyonId}
                  onChange={setLokasyonId}
                  placeholder="Lokasyon seçin..."
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#333' }}>Tarih</label>
                <input
                  type="date"
                  value={tarih}
                  onChange={e => setTarih(e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}
                />
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#333' }}>Malzeme / Reçete *</label>
              <SearchableSelect
                options={malzemeOptions}
                value={secilen}
                onChange={setSecilen}
                placeholder="Malzeme veya reçete arayın..."
                maxVisible={60}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 16, marginBottom: 16 }}>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#333' }}>
                  Miktar *
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={miktar}
                    onChange={e => setMiktar(e.target.value)}
                    placeholder="0.00"
                    style={{ flex: 1, padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: '6px 0 0 6px', fontSize: 14, boxSizing: 'border-box', borderRight: 'none' }}
                  />
                  <span style={{
                    padding: '8px 14px', background: selectedOption ? '#e3f2fd' : '#f5f5f5',
                    border: `1px solid ${colors.border}`, borderRadius: '0 6px 6px 0',
                    fontSize: 14, fontWeight: 700, color: selectedOption ? '#1565c0' : '#999',
                    whiteSpace: 'nowrap', minWidth: 40, textAlign: 'center'
                  }}>
                    {selectedOption ? (selectedOption.birim || 'KG') : 'BR'}
                  </span>
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#333' }}>Zayi Nedeni *</label>
                <SearchableSelect
                  options={nedenOptions}
                  value={nedenKategoriId}
                  onChange={setNedenKategoriId}
                  placeholder="Zayi nedeni seçin..."
                />
                {selectedNeden?.aciklama && (
                  <div style={{ marginTop: 6, fontSize: 12, color: colors.textSecondary }}>
                    {selectedNeden.aciklama}
                  </div>
                )}
              </div>
            </div>

            {/* Giriş guard'ı (kök neden): adet→kg karışmasını önle */}
            {selectedOption && isWeightUnit(selectedOption.birim) && (
              <div style={{ marginBottom: 16 }}>
                {girisAdetUyarisi != null && (
                  <div style={{ padding: '8px 12px', background: '#fff3e0', border: '1px solid #ffb74d', borderRadius: 6, fontSize: 12, color: '#e65100' }}>
                    ⚠️ <b>{selectedOption.label}</b> KG cinsinden — <b>{girisAdetUyarisi} kg</b> (= {girisAdetUyarisi * 1000} g) mı, yoksa <b>{girisAdetUyarisi} adet</b> mi giriyorsun? Adet kastettiysen aşağıdaki çeviriciyle kg'a çevir.
                  </div>
                )}
                <AdetKgHelper birim={selectedOption.birim} onApply={(v) => setMiktar(String(v))} />
              </div>
            )}

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#333' }}>Açıklama / Not</label>
              <input
                type="text"
                value={notText}
                onChange={e => setNotText(e.target.value)}
                placeholder="İsteğe bağlı detay: ör. 3 no'lu tezgah, SKT 2 gün geçmiş"
                style={{ width: '100%', padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}
              />
            </div>

            <button
              onClick={handleSubmit}
              disabled={loading}
              style={{
                padding: '12px 32px', background: loading ? '#999' : '#d32f2f', color: '#fff',
                border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: loading ? 'default' : 'pointer',
                width: '100%'
              }}
            >
              {loading ? 'Kaydediliyor...' : '🗑️ Zayi Kaydet'}
            </button>
          </div>
        )}

        {view === 'list' && (
          <>
            {/* Filtre çubuğu */}
            <div style={{ ...styles.card, padding: 16, marginBottom: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4, color: '#333' }}>Başlangıç</label>
                  <input
                    type="date"
                    value={fTarihBas}
                    onChange={e => setFTarihBas(e.target.value)}
                    style={{ width: '100%', padding: '8px 10px', border: `1px solid ${colors.border}`, borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4, color: '#333' }}>Bitiş</label>
                  <input
                    type="date"
                    value={fTarihBit}
                    onChange={e => setFTarihBit(e.target.value)}
                    style={{ width: '100%', padding: '8px 10px', border: `1px solid ${colors.border}`, borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4, color: '#333' }}>Lokasyon</label>
                  <SearchableSelect
                    options={lokasyonOptions}
                    value={fLokasyon}
                    onChange={setFLokasyon}
                    placeholder="Tüm lokasyonlar"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4, color: '#333' }}>Neden</label>
                  <SearchableSelect
                    options={nedenOptions}
                    value={fNeden}
                    onChange={setFNeden}
                    placeholder="Tüm nedenler"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4, color: '#333' }}>Ara</label>
                  <input
                    type="text"
                    value={fQ}
                    onChange={e => setFQ(e.target.value)}
                    placeholder="Malzeme/neden ara..."
                    style={{ width: '100%', padding: '8px 10px', border: `1px solid ${colors.border}`, borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4, color: '#333' }}>Tür</label>
                  <select
                    value={fTip}
                    onChange={e => setFTip(e.target.value)}
                    style={{ width: '100%', padding: '8px 10px', border: `1px solid ${colors.border}`, borderRadius: 6, fontSize: 13, boxSizing: 'border-box', background: '#fff' }}
                  >
                    <option value="">Tümü</option>
                    <option value="malzeme">Malzeme</option>
                    <option value="recete">Reçete</option>
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
                <button
                  onClick={temizleFiltreler}
                  style={{ padding: '8px 16px', borderRadius: 6, border: `1px solid ${colors.border}`, background: '#fff', color: colors.textSecondary, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
                >Temizle</button>
                <button
                  onClick={indirCSV}
                  style={{ padding: '8px 16px', borderRadius: 6, border: 'none', background: colors.success, color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
                >⬇ CSV İndir</button>
              </div>
            </div>

            {/* Tablo */}
            <div style={{ ...styles.card, padding: 0, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: colors.backgroundAlt, borderBottom: '2px solid #e0e0e0' }}>
                    {[
                      { key: 'tarih', label: 'Tarih', align: 'left' },
                      { key: 'lokasyon_ad', label: 'Lokasyon', align: 'left' },
                      { key: 'malzeme_ad', label: 'Malzeme/Reçete', align: 'left' },
                      { key: 'miktar', label: 'Miktar', align: 'right' },
                      { key: 'neden_kategori_ad', label: 'Neden', align: 'left' },
                    ].map(col => (
                      <th
                        key={col.key}
                        onClick={() => toggleSort(col.key)}
                        style={{ padding: '8px 10px', textAlign: col.align, fontWeight: 600, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                      >
                        {col.label}{sortIndicator(col.key)}
                      </th>
                    ))}
                    <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600, whiteSpace: 'nowrap' }}>İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {gosterilen.length === 0 ? (
                    <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: colors.textTertiary }}>Kayıt bulunamadı</td></tr>
                  ) : gosterilen.map(k => (
                    <tr key={k.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '10px 10px', whiteSpace: 'nowrap' }}>{k.tarih}</td>
                      <td style={{ padding: '10px 10px' }}>{k.lokasyon_ad}</td>
                      <td style={{ padding: '10px 10px', fontWeight: 600 }}>{k.malzeme_ad}</td>
                      <td style={{ padding: '10px 10px', textAlign: 'right', color: colors.danger, fontWeight: 700, whiteSpace: 'nowrap' }}>
                        -{k.miktar} {k.malzeme_birim}
                      </td>
                      <td style={{ padding: '10px 10px' }}>
                        {k.neden_kategori_ad ? (
                          <span style={{
                            display: 'inline-block', padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                            background: (k.neden_kategori_renk || colors.textTertiary) + '22',
                            color: k.neden_kategori_renk || colors.textSecondary,
                            border: `1px solid ${(k.neden_kategori_renk || colors.textTertiary) + '55'}`
                          }}>
                            {k.neden_kategori_ad}
                          </span>
                        ) : (
                          <span style={{ color: colors.textSecondary }}>{k.neden || '—'}</span>
                        )}
                        {k.neden_kategori_ad && k.neden && (
                          <div style={{ marginTop: 3, fontSize: 11, color: colors.textTertiary }}>{k.neden}</div>
                        )}
                      </td>
                      <td style={{ padding: '8px 10px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                        {isEditableRow(k) ? (
                          <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                            <button onClick={() => openEdit(k)} style={rowBtnStyle('#1a73e8')}>Düzenle</button>
                            <button onClick={() => setDeleteRow(k)} style={rowBtnStyle('#d32f2f')}>Sil</button>
                          </div>
                        ) : (
                          <span style={{ color: colors.textTertiary, fontSize: 11 }} title="Eski (legacy) kayıt — uygulamadan düzenlenemez">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* --- Düzenle modalı --- */}
        {editRow && (
          <div
            onClick={() => !actionLoading && setEditRow(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          >
            <div onClick={e => e.stopPropagation()} style={{ ...styles.card, padding: 24, width: '100%', maxWidth: 460, maxHeight: '90vh', overflowY: 'auto' }}>
              <h3 style={{ marginTop: 0, marginBottom: 8, fontSize: 16, fontWeight: 700 }}>✏️ Zayi Düzenle</h3>
              <div style={{ fontSize: 12, color: colors.textSecondary, marginBottom: 16 }}>
                {editRow.tarih} · {editRow.lokasyon_ad} · <b style={{ color: colors.textPrimary }}>{editRow.malzeme_ad}</b>
              </div>

              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#333' }}>Miktar</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                <input
                  type="number" step="0.001" min="0.001" value={eMiktar} disabled={editIsRecipe}
                  onChange={e => setEMiktar(e.target.value)}
                  style={{ flex: 1, padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: '6px 0 0 6px', fontSize: 14, boxSizing: 'border-box', borderRight: 'none', background: editIsRecipe ? '#f5f5f5' : '#fff' }}
                />
                <span style={{ padding: '8px 14px', background: '#e3f2fd', border: `1px solid ${colors.border}`, borderRadius: '0 6px 6px 0', fontSize: 14, fontWeight: 700, color: '#1565c0', whiteSpace: 'nowrap', minWidth: 40, textAlign: 'center' }}>
                  {editRow.malzeme_birim || 'KG'}
                </span>
              </div>
              {editIsRecipe && (
                <div style={{ marginTop: 6, fontSize: 12, color: '#e65100' }}>
                  Reçete (TABAK) zayisinde miktar düzenlenemez; neden/not/tarih düzenlenebilir.
                </div>
              )}
              {!editIsRecipe && (
                <AdetKgHelper birim={editRow.malzeme_birim} onApply={(v) => setEMiktar(String(v))} />
              )}

              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, margin: '16px 0 6px', color: '#333' }}>Zayi Nedeni</label>
              <SearchableSelect options={nedenOptions} value={eNedenKat} onChange={setENedenKat} placeholder="Zayi nedeni seçin..." />

              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, margin: '16px 0 6px', color: '#333' }}>Açıklama / Not</label>
              <input
                type="text" value={eNot} onChange={e => setENot(e.target.value)}
                placeholder="İsteğe bağlı detay"
                style={{ width: '100%', padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}
              />

              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, margin: '16px 0 6px', color: '#333' }}>Tarih</label>
              <input
                type="date" value={eTarih} onChange={e => setETarih(e.target.value)}
                style={{ width: '100%', padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}
              />

              <div style={{ display: 'flex', gap: 8, marginTop: 20, justifyContent: 'flex-end' }}>
                <button onClick={() => setEditRow(null)} disabled={actionLoading}
                  style={{ padding: '8px 18px', borderRadius: 6, border: `1px solid ${colors.border}`, background: '#fff', color: colors.textSecondary, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>Vazgeç</button>
                <button onClick={handleEditSave} disabled={actionLoading}
                  style={{ padding: '8px 18px', borderRadius: 6, border: 'none', background: actionLoading ? '#999' : '#1a73e8', color: '#fff', fontWeight: 700, fontSize: 13, cursor: actionLoading ? 'default' : 'pointer' }}>
                  {actionLoading ? 'Kaydediliyor...' : 'Kaydet'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* --- Silme onayı --- */}
        {deleteRow && (
          <div
            onClick={() => !actionLoading && setDeleteRow(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          >
            <div onClick={e => e.stopPropagation()} style={{ ...styles.card, padding: 24, width: '100%', maxWidth: 420 }}>
              <h3 style={{ marginTop: 0, marginBottom: 12, fontSize: 16, fontWeight: 700, color: colors.danger }}>🗑️ Zayi Kaydını Sil</h3>
              <div style={{ fontSize: 13, marginBottom: 12 }}>
                {deleteRow.tarih} · {deleteRow.lokasyon_ad} · <b>{deleteRow.malzeme_ad}</b> · <span style={{ color: colors.danger, fontWeight: 700 }}>-{deleteRow.miktar} {deleteRow.malzeme_birim}</span>
              </div>
              <div style={{ padding: '8px 12px', background: '#fff3e0', border: '1px solid #ffb74d', borderRadius: 6, fontSize: 12, color: '#e65100', marginBottom: 16 }}>
                Bu kaydı silmek stok hareketini geri alır (stok iade edilir). Devam edilsin mi?
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button onClick={() => setDeleteRow(null)} disabled={actionLoading}
                  style={{ padding: '8px 18px', borderRadius: 6, border: `1px solid ${colors.border}`, background: '#fff', color: colors.textSecondary, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>Vazgeç</button>
                <button onClick={handleDelete} disabled={actionLoading}
                  style={{ padding: '8px 18px', borderRadius: 6, border: 'none', background: actionLoading ? '#999' : '#d32f2f', color: '#fff', fontWeight: 700, fontSize: 13, cursor: actionLoading ? 'default' : 'pointer' }}>
                  {actionLoading ? 'Siliniyor...' : 'Sil'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
